export type AuthScope = "member" | "admin";
export type SessionPersistence = "memory" | "tab";
export type RouteAccess = "public" | "partner" | "member" | "admin";

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface AuthSession {
  readonly scope: AuthScope;
  readonly accessToken: string;
  readonly accessTokenExpiresAt: string;
  readonly refreshToken?: string;
  readonly refreshTokenExpiresAt?: string;
  readonly authorities?: readonly string[];
  readonly adminContext?: {
    readonly actor: { readonly id: number; readonly loginId: string; readonly name: string };
    readonly forcePasswordChange: boolean;
    readonly totpEnrollmentRequired: boolean;
  };
}

export interface PartnerJoinSession {
  readonly sessionToken: string;
  readonly expiresAt: string;
}

export interface SessionChange {
  readonly session: AuthSession | null;
  readonly reason: "set" | "cleared" | "expired";
}

export interface SessionStore {
  get(): AuthSession | null;
  set(session: AuthSession): void;
  clear(reason?: SessionChange["reason"]): void;
  subscribe(listener: (change: SessionChange) => void): () => void;
}

export interface PartnerJoinStore {
  get(): PartnerJoinSession | null;
  set(session: PartnerJoinSession): void;
  clear(): void;
}

export interface CreateSessionStoreOptions {
  readonly scope: AuthScope;
  readonly persistence?: SessionPersistence;
  readonly storage?: StorageLike;
  readonly persistRefreshToken?: boolean;
  readonly now?: () => number;
}

const SESSION_KEY: Record<AuthScope, string> = {
  member: "carepass.member.session.v1",
  admin: "carepass.admin.session.v1",
};

const PARTNER_JOIN_KEY = "carepass.partner.join.v1";

export function parseApiDateTime(value: string): number {
  const normalized = /(?:Z|[+-]\d{2}:\d{2})$/i.test(value) ? value : `${value}Z`;
  return Date.parse(normalized);
}

function isFuture(value: string, now: () => number): boolean {
  const timestamp = parseApiDateTime(value);
  return Number.isFinite(timestamp) && timestamp > now();
}

function isAuthSession(value: unknown, scope: AuthScope): value is AuthSession {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<AuthSession>;
  return candidate.scope === scope
    && typeof candidate.accessToken === "string"
    && candidate.accessToken.length > 0
    && typeof candidate.accessTokenExpiresAt === "string";
}

function readPersistedSession(
  storage: StorageLike | undefined,
  key: string,
  scope: AuthScope,
): AuthSession | null {
  const serialized = storage?.getItem(key);
  if (!serialized) return null;
  try {
    const parsed: unknown = JSON.parse(serialized);
    if (isAuthSession(parsed, scope)) return parsed;
  } catch {
    // Invalid browser state is discarded below.
  }
  storage?.removeItem(key);
  return null;
}

export function createSessionStore(options: CreateSessionStoreOptions): SessionStore {
  const persistence = options.persistence ?? "memory";
  if (persistence === "tab" && !options.storage) {
    throw new Error("Tab session persistence requires a sessionStorage-compatible adapter.");
  }

  const now = options.now ?? Date.now;
  const key = SESSION_KEY[options.scope];
  const listeners = new Set<(change: SessionChange) => void>();
  let memorySession: AuthSession | null = null;

  const notify = (change: SessionChange) => {
    for (const listener of listeners) listener(change);
  };

  const clear = (reason: SessionChange["reason"] = "cleared") => {
    memorySession = null;
    options.storage?.removeItem(key);
    notify({ session: null, reason });
  };

  return {
    get() {
      const session = memorySession ?? (persistence === "tab"
        ? readPersistedSession(options.storage, key, options.scope)
        : null);
      if (!session) return null;
      if (!isFuture(session.accessTokenExpiresAt, now)) {
        clear("expired");
        return null;
      }
      memorySession = session;
      return session;
    },
    set(session) {
      if (session.scope !== options.scope) {
        throw new Error(`Cannot store a ${session.scope} session in the ${options.scope} session store.`);
      }
      if (!isFuture(session.accessTokenExpiresAt, now)) {
        throw new Error("Cannot store an already expired access token.");
      }
      memorySession = session;
      if (persistence === "tab") {
        const persisted = options.persistRefreshToken
          ? session
          : { ...session, refreshToken: undefined, refreshTokenExpiresAt: undefined };
        options.storage?.setItem(key, JSON.stringify(persisted));
      }
      notify({ session, reason: "set" });
    },
    clear,
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

function isPartnerJoinSession(value: unknown): value is PartnerJoinSession {
  if (!value || typeof value !== "object") return false;
  const candidate = value as Partial<PartnerJoinSession>;
  return typeof candidate.sessionToken === "string"
    && candidate.sessionToken.length > 0
    && typeof candidate.expiresAt === "string";
}

export function createPartnerJoinStore(
  storage: StorageLike,
  now: () => number = Date.now,
  key: string = PARTNER_JOIN_KEY,
): PartnerJoinStore {
  return {
    get() {
      const serialized = storage.getItem(key);
      if (!serialized) return null;
      try {
        const parsed: unknown = JSON.parse(serialized);
        if (isPartnerJoinSession(parsed) && isFuture(parsed.expiresAt, now)) return parsed;
      } catch {
        // Invalid browser state is discarded below.
      }
      storage.removeItem(key);
      return null;
    },
    set(session) {
      if (!isFuture(session.expiresAt, now)) {
        throw new Error("Cannot store an expired partner join session.");
      }
      storage.setItem(key, JSON.stringify(session));
    },
    clear() {
      storage.removeItem(key);
    },
  };
}

export type AccessDecision = "allowed" | "unauthenticated" | "forbidden";

export interface AuthorityRequirement {
  readonly all?: readonly string[];
  readonly any?: readonly string[];
}

export function authorizeRoute(
  access: RouteAccess,
  session: AuthSession | null,
  requirement: readonly string[] | AuthorityRequirement = [],
): AccessDecision {
  if (access === "public" || access === "partner") return "allowed";
  if (!session || session.scope !== access) return "unauthenticated";

  const granted = new Set(session.authorities ?? []);
  const normalized: AuthorityRequirement = Array.isArray(requirement)
    ? { all: requirement }
    : requirement as AuthorityRequirement;
  const requiredAll = normalized.all ?? [];
  const requiredAny = normalized.any ?? [];
  const hasAll = requiredAll.every((authority) => granted.has(authority));
  const hasAny = requiredAny.length === 0
    || requiredAny.some((authority) => granted.has(authority));
  return hasAll && hasAny
    ? "allowed"
    : "forbidden";
}

export function bearerToken(sessionStore: SessionStore): string | null {
  return sessionStore.get()?.accessToken ?? null;
}
